import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import Topbar from "../../components/topbar/Topbar";
import toast from "react-hot-toast";
import "./adminuserdetail.css";
import UserOnboarding from "./UserOnboarding";
import PhotoLightbox from "../../components/PhotoLightbox/PhotoLightbox";
import UserPostHog from "./UserPostHog";
import { jobDisplayTitle } from "../../utils/jobTitle";

function formatRel(date) {
  if (!date) return "—";
  const ms = Date.now() - new Date(date).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatDuration(sec) {
  if (!sec) return "—";
  const total = Math.floor(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}h ${m}m`
    : m > 0
    ? `${m}m ${s}s`
    : `${s}s`;
}

function ImgWithFallback({ src, alt, className, fallbackClassName, onClick }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return <div className={fallbackClassName || className} aria-label={alt} />;
  }
  return (
    <img
      src={src}
      alt={alt || ""}
      className={`${className}${onClick ? " plb-zoomable" : ""}`}
      onClick={onClick}
      onError={() => setErrored(true)}
    />
  );
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const { user: me } = useContext(AuthContext);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giftAmount, setGiftAmount] = useState("");
  const [gifting, setGifting] = useState(false);
  const [removeAmount, setRemoveAmount] = useState("");
  const [removing, setRemoving] = useState(false);
  const [watchClip, setWatchClip] = useState(null); // { url, hook }
  const [photoOpen, setPhotoOpen] = useState(false);

  useEffect(() => { document.title = "Clipbait — User detail"; }, []);

  useEffect(() => {
    if (!me) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, id]);

  async function handleGiftCredits() {
    const amount = parseInt(giftAmount, 10);
    if (!amount || amount <= 0) {
      toast.error("Enter a positive integer");
      return;
    }
    setGifting(true);
    try {
      const res = await axiosInstance.post(`/admin/analytics/users/${id}/gift-credits`, { amount });
      toast.success(`Gifted ${res.data.granted} credits — new balance ${res.data.newBalance}`);
      setGiftAmount("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Gift failed");
    } finally {
      setGifting(false);
    }
  }

  async function handleRemoveCredits() {
    const amount = parseInt(removeAmount, 10);
    if (!amount || amount <= 0) {
      toast.error("Enter a positive integer");
      return;
    }
    if (!window.confirm(`Remove ${amount} credits from this user?`)) return;
    setRemoving(true);
    try {
      const res = await axiosInstance.post(`/admin/analytics/users/${id}/remove-credits`, { amount });
      toast.success(`Removed ${res.data.removed} credits — new balance ${res.data.newBalance}`);
      setRemoveAmount("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Remove failed");
    } finally {
      setRemoving(false);
    }
  }

  // silent = background refresh (no full-page "Loading…") for live progress.
  async function load(silent = false) {
    if (!silent) setLoading(true);
    try {
      const res = await axiosInstance.get(`/admin/analytics/users/${id}`);
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin only");
        navigate("/");
        return;
      }
      if (err.response?.status === 404) {
        toast.error("User not found");
        navigate("/admin/dashboard?tab=users");
        return;
      }
      if (!silent) toast.error(err.response?.data?.error || "Failed to load user");
    } finally {
      if (!silent) setLoading(false);
    }
  }

  // While something is clipping for this user, refresh every 4s so progress,
  // step and clips-found update live.
  const running = !!data && (
    (data.jobs || []).some((j) => j.status === "queued" || j.status === "processing") ||
    (data.liveSessions || []).some((s) => s.status === "active" || s.status === "starting")
  );
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => load(true), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, id]);

  if (loading || !data) {
    return (
      <>
        <Topbar />
        <div className="aud-page">
          <div className="aud-container">
            <div className="aud-empty">Loading…</div>
          </div>
        </div>
      </>
    );
  }

  const { user, jobStats, jobs, liveSessions = [], liveStats, downloads = [], downloadCount = 0 } = data;

  // Live auto-clip sessions store clips outside ClipJob, so they don't show in
  // the job stats/list. Map them into the JobCard shape so they render the same
  // (thumbnails + click-to-watch) under their own section.
  const liveAsJobs = liveSessions.map((s) => ({
    _id: s._id,
    videoTitle: s.videoTitle || s.streamerName || "Live session",
    videoUrl: s.channelUrl,
    videoThumbnail: s.videoThumbnail,
    platform: s.platform,
    status: s.endedReason ? `${s.status} (${s.endedReason})` : s.status,
    streamerName: s.streamerName,
    createdAt: s.createdAt,
    clipCount: s.clipCount,
    clips: (s.clips || []).map((c) => ({
      hook: c.hook,
      duration: c.duration,
      start_sec: c.broadcastStartSec,
      url: c.url,
      thumbnailUrl: c.thumbnailUrl,
    })),
  }));

  // Top stat cards should reflect live auto-clips too (a session counts as a
  // job; ended = completed, error = failed; its clips count toward total clips)
  // so an auto-clip-only user doesn't read all zeros despite real activity.
  const stats = {
    totalJobs: (jobStats.totalJobs || 0) + liveSessions.length,
    completedJobs:
      (jobStats.completedJobs || 0) +
      liveSessions.filter((s) => s.status === "ended").length,
    failedJobs:
      (jobStats.failedJobs || 0) +
      liveSessions.filter((s) => s.status === "error").length,
    totalClips: (jobStats.totalClips || 0) + (liveStats?.totalClips || 0),
    totalSourceSeconds: jobStats.totalSourceSeconds || 0,
  };
  const brands = user.brands || [];
  const usage = user.usage || {};

  return (
    <>
      <Topbar />
      <div className="aud-page">
        <div className="aud-container">
          <div className="aud-back">
            <Link to="/admin/dashboard?tab=users">← Back to users</Link>
          </div>

          {/* Header */}
          <div className="aud-header">
            <ImgWithFallback
              src={user.profilePicture}
              alt={user.username}
              className="aud-avatar"
              fallbackClassName="aud-avatar aud-avatar--blank"
              onClick={() => setPhotoOpen(true)}
            />
            {photoOpen && (
              <PhotoLightbox src={user.profilePicture} name={user.username} onClose={() => setPhotoOpen(false)} />
            )}
            <div className="aud-header-meta">
              <h1>
                {user.username || "—"}
                {user.isAdmin && <span className="chip chip-admin">admin</span>}
                {!user.verified && <span className="chip chip-warn">unverified</span>}
                {user.plan && user.plan !== "none" && (
                  <span className="chip chip-plan">{user.plan}</span>
                )}
              </h1>
              <div className="aud-email">{user.email}</div>
              <div className="aud-sub">
                📱 {user.phoneNumber ? (
                  <>
                    <strong style={{ color: "#e4e4e7" }}>{user.phoneNumber}</strong>
                    {user.smsOptedOut && <span style={{ color: "#f87171" }}> · opted out of texts</span>}
                  </>
                ) : (
                  <span style={{ color: "#a1a1aa" }}>no phone number</span>
                )}
              </div>
              <div className="aud-sub">
                Joined {formatRel(user.createdAt)} · Last sign-in {formatRel(user.lastSignInAt)}
              </div>
              {user.geo?.country && (
                <div className="aud-sub">
                  📍 {[user.geo.city, user.geo.region, user.geo.country]
                    .filter(Boolean)
                    .join(", ")}
                </div>
              )}
              {/* "How did you find Clipbait?" onboarding answer (required question) */}
              <div className="aud-sub">
                🔎 Found us via:{" "}
                {user.onboarding?.foundVia ? (
                  <strong style={{ color: "#e4e4e7", textTransform: "capitalize" }}>
                    {user.onboarding.foundVia === "other"
                      ? `Other — ${user.onboarding.foundViaOther || "(unspecified)"}`
                      : user.onboarding.foundVia}
                  </strong>
                ) : (
                  <span style={{ color: "#a1a1aa" }}>not answered</span>
                )}
              </div>
              {/* Public profile link removed — public profile pages aren't
                  yet shipped for clippers/streamers in the new app. */}
            </div>
          </div>

          {/* Live banner — what's clipping for them right now */}
          <RunningNow jobs={jobs} liveSessions={liveSessions} />

          {/* Clip downloads — the clearest sign a clip landed (models/ClipDownload) */}
          <div className="aud-section">
            <h3>Downloads ({downloadCount})</h3>
            {downloads.length === 0 ? (
              <div className="muted">No downloads yet.</div>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {downloads.map((d) => (
                  <li key={d._id} style={{ display: "flex", gap: 10, fontSize: 13, alignItems: "baseline" }}>
                    <span className="muted" style={{ minWidth: 120 }} title={new Date(d.createdAt).toLocaleString()}>
                      {formatRel(d.createdAt)}
                    </span>
                    <span style={{ color: "#e4e4e7", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {d.hook || (d.clipIndex != null ? `Clip ${d.clipIndex + 1}` : "Clip")}
                    </span>
                    {d.url && <a href={d.url} target="_blank" rel="noreferrer" style={{ color: "#ff8a3d" }}>open ↗</a>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Stats */}
          <div className="aud-stats">
            <Stat label="Total jobs"      value={stats.totalJobs} />
            <Stat label="Completed"       value={stats.completedJobs} accent="green" />
            <Stat label="Failed"          value={stats.failedJobs}    accent="red" />
            <Stat label="Total clips"     value={stats.totalClips}    accent="purple" />
            <Stat label="Source minutes"  value={Math.floor((stats.totalSourceSeconds || 0) / 60)} />
            <Stat label="Credits"         value={user.credits ?? 0} accent="gold" />
          </div>

          {/* What they answered in the onboarding quiz + the plan it promised. */}
          <UserOnboarding user={user} />

          {/* Behavior from PostHog: first touch, location/device, funnel,
              screen recordings, recent activity. */}
          <UserPostHog userId={id} />

          {/* Gift credits — pushes into User.creditGrants so the next clip
              generation doesn't wipe the gift (manual user.credits edits
              get clobbered by the bucket-recalc on deduction). */}
          <div className="aud-section aud-gift-section">
            <h2>🎁 Gift credits</h2>
            <div className="aud-gift-row">
              <input
                type="number"
                min="1"
                step="1"
                placeholder="Amount (e.g. 100)"
                value={giftAmount}
                onChange={(e) => setGiftAmount(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleGiftCredits(); }}
                className="aud-gift-input"
                disabled={gifting}
              />
              <button
                className="aud-gift-btn"
                onClick={handleGiftCredits}
                disabled={gifting || !giftAmount}
              >
                {gifting ? "Gifting…" : `Gift ${giftAmount || "?"} credits`}
              </button>
            </div>
            <div className="aud-gift-hint muted">
              Quick-add: {[10, 50, 100, 500, 1000].map((n) => (
                <button
                  key={n}
                  className="aud-gift-chip"
                  onClick={() => setGiftAmount(String(n))}
                  disabled={gifting}
                >+{n}</button>
              ))}
            </div>
            <div className="aud-gift-row" style={{ marginTop: 12 }}>
              <input
                type="number"
                min="1"
                placeholder="Credits to remove"
                value={removeAmount}
                onChange={(e) => setRemoveAmount(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleRemoveCredits(); }}
                className="aud-gift-input"
                disabled={removing}
              />
              <button
                className="aud-gift-btn"
                style={{ background: "#b42318" }}
                onClick={handleRemoveCredits}
                disabled={removing || !removeAmount}
              >
                {removing ? "Removing…" : `Remove ${removeAmount || "?"} credits`}
              </button>
            </div>
          </div>

          {/* Usage from User document */}
          {Object.keys(usage).length > 0 && (
            <div className="aud-section">
              <h2>Usage (tracked counters)</h2>
              <div className="aud-usage">
                {usage.videosProcessed != null && <div><b>{usage.videosProcessed}</b> videos processed</div>}
                {usage.clipsGenerated != null && <div><b>{usage.clipsGenerated}</b> clips generated</div>}
                {usage.minutesProcessed != null && <div><b>{usage.minutesProcessed}</b> min processed</div>}
              </div>
            </div>
          )}

          {/* Brands */}
          <div className="aud-section">
            <h2>Brands ({brands.length})</h2>
            {brands.length === 0 ? (
              <div className="aud-empty-sm">No brands set up yet.</div>
            ) : (
              <div className="aud-brands">
                {brands.map((b) => (
                  <div key={b._id} className="aud-brand-card">
                    <div className="aud-brand-head">
                      <ImgWithFallback
                        src={b.avatar}
                        alt={b.name}
                        className="aud-brand-avatar"
                        fallbackClassName="aud-brand-avatar aud-brand-avatar--blank"
                      />
                      <div>
                        <div className="aud-brand-name">{b.name}</div>
                        <div className="aud-brand-sub">
                          {b.creatorSettings?.platform || "no platform"}
                          {b.creatorSettings?.streamType ? ` · ${b.creatorSettings.streamType}` : ""}
                        </div>
                      </div>
                      {String(user.activeBrandId) === String(b._id) && (
                        <span className="chip chip-active">active</span>
                      )}
                    </div>
                    {b.creatorSettings?.channelUsername && (
                      <div className="aud-brand-row">
                        <span className="muted">Channel:</span> @{b.creatorSettings.channelUsername}
                      </div>
                    )}
                    <div className="aud-brand-row aud-brand-sub-muted">
                      Created {formatRel(b.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent clip jobs */}
          <div className="aud-section">
            <h2>Recent clip jobs ({jobs.length})</h2>
            {jobs.length === 0 ? (
              <div className="aud-empty-sm">No clip jobs yet.</div>
            ) : (
              <div className="aud-jobs">
                {jobs.map((j) => (
                  <JobCard key={j._id} job={j} userId={id} onWatchClip={setWatchClip} onRerun={() => load(true)} />
                ))}
              </div>
            )}
          </div>

          {/* Live auto-clip sessions — clips from the Twitch/Kick/YouTube live
              auto-clipper (stored separately from ClipJob, so the job stats
              above read 0 for auto-clip-only users). */}
          {liveSessions.length > 0 && (
            <div className="aud-section">
              <h2>Live auto-clips ({liveStats?.totalClips ?? 0} clips · {liveSessions.length} session{liveSessions.length === 1 ? "" : "s"})</h2>
              <div className="aud-jobs">
                {liveAsJobs.map((j) => (
                  <JobCard key={j._id} job={j} onWatchClip={setWatchClip} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Watch-clip modal — fullscreen video overlay */}
      {watchClip && (
        <div className="aud-watch-overlay" onClick={() => setWatchClip(null)}>
          <button
            className="aud-watch-close"
            onClick={(e) => { e.stopPropagation(); setWatchClip(null); }}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="aud-watch-inner" onClick={(e) => e.stopPropagation()}>
            <video
              src={watchClip.url}
              controls
              autoPlay
              playsInline
              className="aud-watch-video"
            />
            {watchClip.hook && (
              <div className="aud-watch-caption">{watchClip.hook}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`aud-stat ${accent ? `aud-stat--${accent}` : ""}`}>
      <div className="aud-stat-label">{label}</div>
      <div className="aud-stat-value">{value}</div>
    </div>
  );
}

const fmtEta = (sec) => {
  if (!sec || sec <= 0) return null;
  if (sec < 60) return `${Math.round(sec)}s left`;
  return `~${Math.ceil(sec / 60)} min left`;
};
const prettyStep = (s) => (s ? String(s).replace(/[_-]+/g, " ").replace(/^./, (c) => c.toUpperCase()) : null);

// Animated progress for a running clip: bar, step, time left, clips so far.
function JobProgress({ job }) {
  const pct = Math.max(job.status === "queued" ? 2 : 4, Math.min(100, Math.round(job.progress || 0)));
  return (
    <div className="aud-progress">
      <div className="aud-progress-track">
        <div className="aud-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="aud-progress-meta">
        <span className="aud-progress-step">
          {job.status === "queued" ? "Waiting for a worker…" : prettyStep(job.currentStep) || "Working…"}
        </span>
        <span className="muted">
          {[job.clipCount ? `${job.clipCount} clip${job.clipCount === 1 ? "" : "s"} so far` : null, fmtEta(job.etaSeconds)]
            .filter(Boolean)
            .join(" · ") || `${pct}%`}
        </span>
      </div>
    </div>
  );
}

// Banner at the top of the profile while something is clipping for them.
function RunningNow({ jobs = [], liveSessions = [] }) {
  const runningJobs = jobs.filter((j) => j.status === "queued" || j.status === "processing");
  const liveNow = liveSessions.filter((s) => s.status === "active" || s.status === "starting");
  if (!runningJobs.length && !liveNow.length) return null;
  return (
    <div className="aud-running">
      <div className="aud-running-head">
        <span className="aud-live-dot aud-live-dot--lg" />
        Clipping right now
        <span className="muted aud-running-sub">updates every few seconds</span>
      </div>
      {liveNow.map((s) => (
        <div key={s._id} className="aud-running-row">
          <span className="aud-running-title">🔴 Live: {s.videoTitle || s.channelUrl || s.streamerName}</span>
          <span className="muted">{s.platform} · {(s.clips || []).length} clips so far</span>
        </div>
      ))}
      {runningJobs.map((j) => (
        <div key={j._id} className="aud-running-row">
          <span className="aud-running-title">{jobDisplayTitle(j)}</span>
          <JobProgress job={j} />
        </div>
      ))}
    </div>
  );
}

function JobCard({ job, userId, onWatchClip, onRerun }) {
  const [open, setOpen] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const canRerun = !!userId && (job.status === "cancelled" || job.status === "failed");

  // Support: start this video again for the user, on the house (no credits).
  const rerunFree = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Re-run this video for the user for free? No credits will be charged.")) return;
    setRerunning(true);
    try {
      const r = await axiosInstance.post(`/admin/analytics/users/${userId}/jobs/${job._id}/rerun-free`);
      toast.success(r.data?.duplicate ? "Already re-running" : "Re-running for free 🎁");
      onRerun?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Couldn't re-run");
    } finally {
      setRerunning(false);
    }
  };
  const statusClass =
    job.status === "completed" ? "status-green"
    : job.status === "failed"  ? "status-red"
    : "status-yellow";
  const isRunning = job.status === "queued" || job.status === "processing";

  return (
    <div className="aud-job-card">
      <div className="aud-job-head" onClick={() => setOpen((o) => !o)}>
        <ImgWithFallback
          src={job.videoThumbnail}
          alt=""
          className="aud-job-thumb"
          fallbackClassName="aud-job-thumb aud-job-thumb--blank"
        />
        <div className="aud-job-meta">
          <div className="aud-job-title">{jobDisplayTitle(job)}</div>
          <div className="aud-job-sub">
            {job.platform} · {job.clipCount} clips · {job.videoAuthor || job.streamerName || "—"}
            {job.videoDuration ? ` · ${formatDuration(job.videoDuration)}` : ""}
          </div>
        </div>
        <div className="aud-job-side">
          {isRunning ? (
            <span className="chip aud-chip-live"><span className="aud-live-dot" />{job.status === "queued" ? "Starting" : `Clipping ${Math.round(job.progress || 0)}%`}</span>
          ) : (
            <span className={`chip ${statusClass}`}>{job.status}</span>
          )}
          {job.complimentary && <span className="chip status-green" title="Re-run free by an admin — no credits charged">🎁 free</span>}
          <span className="aud-job-date">{formatRel(job.createdAt)}</span>
          {canRerun && (
            <button type="button" className="aud-rerun-btn" onClick={rerunFree} disabled={rerunning}>
              {rerunning ? "Starting…" : "Re-run free"}
            </button>
          )}
        </div>
      </div>
      {isRunning && <JobProgress job={job} />}
      {job.status === "failed" && job.error && (
        <div className="aud-job-error">⚠️ {String(job.error).slice(0, 220)}</div>
      )}
      {open && (
        <div className="aud-job-body">
          <div className="aud-job-info-row">
            <span className="muted">Source:</span>{" "}
            <a href={job.videoUrl} target="_blank" rel="noreferrer">{job.videoUrl}</a>
          </div>
          {job.processingTime && (
            <div className="aud-job-info-row">
              <span className="muted">Processing:</span> {job.processingTime}s
            </div>
          )}
          {job.aspectRatio && (
            <div className="aud-job-info-row">
              <span className="muted">Aspect:</span> {job.aspectRatio}
            </div>
          )}
          {job.clips && job.clips.length > 0 && (
            <div className="aud-job-clips">
              {job.clips.map((c, idx) => (
                <div
                  key={idx}
                  className={`aud-clip-mini ${c.url ? "aud-clip-mini--clickable" : ""}`}
                  onClick={() => c.url && onWatchClip?.({ url: c.url, hook: c.hook })}
                  title={c.url ? "Click to watch" : ""}
                >
                  {c.thumbnailUrl ? (
                    <ImgWithFallback
                      src={c.thumbnailUrl}
                      alt=""
                      className="aud-clip-mini-thumb"
                      fallbackClassName="aud-clip-mini-thumb aud-clip-mini-thumb--blank"
                    />
                  ) : (
                    <div className="aud-clip-mini-thumb aud-clip-mini-thumb--blank" />
                  )}
                  {c.url && (
                    <div className="aud-clip-mini-play">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                    </div>
                  )}
                  <div className="aud-clip-mini-meta">
                    <div className="aud-clip-mini-hook">{c.hook || "(no hook)"}</div>
                    <div className="aud-clip-mini-sub muted">
                      {c.duration ? `${Math.round(c.duration)}s` : "—"}
                      {c.start_sec != null ? ` · @${Math.floor(c.start_sec)}s` : ""}
                    </div>
                  </div>
                </div>
              ))}
              {job.clipCount > job.clips.length && (
                <div className="aud-clip-mini muted">
                  + {job.clipCount - job.clips.length} more
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
